"use client";

import type React from "react";

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  CheckCircle,
  Loader2,
} from "lucide-react";
import Link from "next/link";

export default function ContactPageClient() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    projectType: [] as string[],
    message: "",
    urgency: "",
  });

  // Honeypot field - if filled, it's likely a bot
  const [honeypotField, setHoneypotField] = useState("");

  const [formState, setFormState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null as string | null,
  });

  // Email validation with regex
  const isValidEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const projectTypes = [
    "Kraninstallation",
    "Service och underhåll",
    "Radiosystem",
    "Svetsning och smide",
    "Reparationer",
    "Konsultation",
  ];

  const handleCheckboxChange = (value: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      projectType: checked
        ? [...prev.projectType, value]
        : prev.projectType.filter((item) => item !== value),
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    // Handle honeypot field separately
    if (name === "website") {
      setHoneypotField(value);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - if field is filled, it's likely a bot
    // We silently "succeed" to not alert the bot that we detected it
    if (honeypotField) {
      if (process.env.NODE_ENV === "development") {
        console.log("Bot submission detected via honeypot");
      }
      // Simulate success but don't actually submit
      // Add a small delay to make the bot think we're processing something
      setTimeout(() => {
        setFormState({
          isSubmitting: false,
          isSubmitted: true, // Show success message to the bot
          error: null,
        });
      }, 1000); // Fake delay for realism
      return;
    }

    // Enhanced validation
    if (!formData.name || formData.name.trim().length < 2) {
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error: "Ange ett giltigt namn",
      });
      return;
    }

    if (!formData.email || !isValidEmail(formData.email)) {
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error: "Ange en giltig e-postadress",
      });
      return;
    }

    if (!formData.message || formData.message.trim().length < 5) {
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error: "Meddelandet måste innehålla minst 5 tecken",
      });
      return;
    }

    try {
      setFormState({
        isSubmitting: true,
        isSubmitted: false,
        error: null,
      });

      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Ett fel uppstod vid skickandet av formuläret",
        );
      }

      // Clear form on success
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        projectType: [],
        message: "",
        urgency: "",
      });

      // Also clear the honeypot field
      setHoneypotField("");

      setFormState({
        isSubmitting: false,
        isSubmitted: true,
        error: null,
      });
    } catch (error) {
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error:
          error instanceof Error
            ? error.message
            : "Ett fel uppstod vid skickandet av formuläret",
      });
      console.error("Error submitting form:", error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 gradient-hero-medium">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              <span className="text-blue-600">Kostnadsfri offert</span>
              <br />
              Svar inom 24 timmar
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Få en kostnadsfri offert för ditt projekt.
              <br />
              Vi svarar inom 24 timmar och erbjuder professionell rådgivning.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Badge
                variant="secondary"
                className="px-4 py-2 bg-blue-600 text-white border border-gray-200"
              >
                <Clock className="w-4 h-4 mr-2" />
                Svar inom 24h
              </Badge>
              <Badge
                variant="secondary"
                className="px-4 py-2 bg-blue-600 text-white border border-gray-200"
              >
                <Phone className="w-4 h-4 mr-2" />
                Kostnadsfri konsultation
              </Badge>
              <Badge
                variant="secondary"
                className="px-4 py-2 bg-blue-600 text-white border border-gray-200"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Hela Sverige
              </Badge>
            </div>
          </div>
        </div>
      </section>

      <div className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">
                  Skicka Förfrågan
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Fyll i formuläret så kontaktar vi dig inom 24 timmar med en
                  kostnadsfri offert.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  data-lpignore="true"
                >
                  {/* Honeypot field - hidden from real users but visible to bots */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <Input
                      type="text"
                      id="website"
                      name="website"
                      value={honeypotField}
                      onChange={handleInputChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  {/* Show success message if form was submitted successfully */}
                  {formState.isSubmitted && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-green-700 font-medium flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Tack för din förfrågan! Vi återkommer inom 24 timmar.
                      </p>
                    </div>
                  )}

                  {/* Show error message if there was an error */}
                  {formState.error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-700 font-medium">
                        {formState.error}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="name"
                        className="text-gray-700 font-medium mb-1"
                      >
                        Namn *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Ditt namn"
                        className="bg-gray-50 border-gray-300"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={formState.isSubmitting}
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="email"
                        className="text-gray-700 font-medium mb-1"
                      >
                        E-post *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="din@epost.se"
                        className="bg-gray-50 border-gray-300"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={formState.isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="phone"
                        className="text-gray-700 font-medium mb-1"
                      >
                        Telefon
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Ditt telefonnummer"
                        className="bg-gray-50 border-gray-300"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={formState.isSubmitting}
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="company"
                        className="text-gray-900 font-medium"
                      >
                        Företag
                      </Label>
                      <Input
                        id="company"
                        placeholder="Företagsnamn"
                        className="bg-gray-50 border-gray-300 mt-1"
                        value={formData.company}
                        data-lpignore="true"
                        onChange={(e) =>
                          setFormData({ ...formData, company: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label
                      htmlFor="project-type"
                      className="text-gray-700 font-medium mb-1"
                    >
                      Typ av projekt
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {projectTypes.map((type) => (
                        <div
                          key={type}
                          className="flex items-center space-x-2 bg-gray-50 border border-gray-200 p-2 rounded-md"
                        >
                          <Checkbox
                            id={`project-${type}`}
                            checked={formData.projectType.includes(type)}
                            onCheckedChange={(checked) =>
                              handleCheckboxChange(type, Boolean(checked))
                            }
                            disabled={formState.isSubmitting}
                          />
                          <label
                            htmlFor={`project-${type}`}
                            className="text-gray-700 cursor-pointer select-none text-sm"
                          >
                            {type}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="urgency"
                      className="text-gray-700 font-medium mb-1"
                    >
                      Hur brådskande är ditt projekt?
                    </Label>
                    <select
                      id="urgency"
                      name="urgency"
                      className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md p-2.5"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      disabled={formState.isSubmitting}
                    >
                      <option value="">Välj tidsram</option>
                      <option value="Omgående (inom en vecka)">
                        Omgående (inom en vecka)
                      </option>
                      <option value="Inom en månad">Inom en månad</option>
                      <option value="Inom 2-3 månader">Inom 2-3 månader</option>
                      <option value="Framtida projekt (3+ månader)">
                        Framtida projekt (3+ månader)
                      </option>
                    </select>
                  </div>

                  <div>
                    <Label
                      htmlFor="message"
                      className="text-gray-700 font-medium mb-1"
                    >
                      Meddelande *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Beskriv ditt projekt eller din förfrågan"
                      className="min-h-32 bg-gray-50 border-gray-300"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      disabled={formState.isSubmitting}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                    disabled={formState.isSubmitting}
                  >
                    {formState.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Skickar...
                      </>
                    ) : (
                      "Skicka förfrågan"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">
                    Kontaktinformation
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    Vi svarar på förfrågningar inom 24 timmar
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-8">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-3">
                        Huvudkontor
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-blue-600 mr-3" />
                          <span className="text-gray-700">
                            Låsblecksgatan 6
                            <br />
                            589 41 Linköping
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 text-blue-600 mr-3" />
                          <a
                            href="tel:0709380356"
                            className="text-gray-700 hover:text-blue-600"
                          >
                            070-938 03 56
                          </a>
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 text-blue-600 mr-3" />
                          <a
                            href="mailto:info@axfalk.com"
                            className="text-gray-700 hover:text-blue-600"
                          >
                            info@axfalk.com
                          </a>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-3">
                        Kontaktpersoner
                      </h3>

                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900">
                          Gunnleif á Deild
                        </h4>
                        <p className="text-gray-600 text-sm mb-2">
                          VD & Grundare
                        </p>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 text-blue-600 mr-3" />
                          <a
                            href="tel:0709380356"
                            className="text-gray-700 hover:text-blue-600"
                          >
                            070-938 03 56
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">
                    Våra Samarbetspartners
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700">Craneteam</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700">Lyftab</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700">
                        Linköpings Byggdetaljer AB
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Vårt Verksamhetsområde
                      </h3>
                      <p className="text-gray-700">
                        Vi utgår från Linköping och arbetar främst i
                        Östergötland, men har genomfört uppdrag över hela
                        Sverige.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-400 via-blue-700 to-blue-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Föredrar du att ringa direkt?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Ring oss för omedelbar vägledning om du behöver en snabb lösning.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
            <Link
              href="tel:0709380356"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-blue-50 transition-all duration-200 min-h-[56px] border-2 border-white shadow-lg hover:shadow-xl no-underline"
            >
              <Phone className="h-5 w-5" />
              Ring: 070-938 03 56
            </Link>
            <Link
              href="mailto:info@axfalk.com"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white px-8 py-4 rounded-xl font-semibold text-lg border-2 border-white hover:bg-white hover:text-blue-600 transition-all duration-200 min-h-[56px] shadow-lg hover:shadow-xl no-underline"
            >
              <Mail className="h-5 w-5" />
              Skicka E-post
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
